'use client';

import React, { useEffect, useState } from 'react';
import QRCodeDisplay from './QRCodeDisplay';
import CountdownRing from './CountdownRing';

interface KioskQRPanelProps {
  qrUrl: string;
  secondsRemaining: number;
  ttlSeconds: number;
  tokenVersion: number;
  isRefreshing: boolean;
}

export default function KioskQRPanel({
  qrUrl,
  secondsRemaining,
  ttlSeconds,
  tokenVersion,
  isRefreshing,
}: KioskQRPanelProps) {
  const [visible, setVisible] = useState(true);

  // Brief fade on QR refresh
  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 250);
    return () => clearTimeout(t);
  }, [tokenVersion]);

  const isUrgent = secondsRemaining <= 8;

  return (
    <div className="flex flex-col items-center gap-8 max-w-2xl w-full">
      {/* Instruction */}
      <div className="text-center">
        <p className="kiosk-muted text-xl font-medium tracking-wide">
          Scan with your phone to
        </p>
        <p className="text-kiosk-blue text-3xl font-bold mt-1 tracking-tight">
          Clock In or Clock Out
        </p>
      </div>

      {/* QR code container */}
      <div className="relative flex items-center justify-center">
        {/* Outer glow ring */}
        <div
          className={`absolute inset-0 rounded-3xl qr-pulse-ring ${isUrgent ? 'opacity-0' : ''}`}
          style={{
            background: 'radial-gradient(circle, rgba(26,79,160,0.18) 0%, transparent 70%)',
            transform: 'scale(1.15)',
          }}
        />

        {/* QR card */}
        <div
          className={`relative kiosk-surface rounded-3xl p-8 border-2 transition-all duration-300 ${
            isUrgent
              ? 'border-amber-500 border-opacity-60' :'kiosk-border'
          } ${visible ? 'opacity-100' : 'opacity-0'}`}
          style={{
            boxShadow: isUrgent
              ? '0 0 40px rgba(240, 165, 0, 0.2), 0 0 80px rgba(240, 165, 0, 0.08)'
              : '0 0 40px rgba(26, 79, 160, 0.2), 0 0 80px rgba(26, 79, 160, 0.08)',
            transition: 'opacity 0.25s ease, box-shadow 0.5s ease, border-color 0.5s ease',
          }}
        >
          {isRefreshing ? (
            <div className="w-72 h-72 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-blue-900 border-t-blue-400 rounded-full animate-spin" />
            </div>
          ) : (
            <QRCodeDisplay url={qrUrl} size={288} />
          )}

          {/* Corner brackets for visual polish */}
          <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-blue-500 border-opacity-50 rounded-tl-sm" />
          <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-blue-500 border-opacity-50 rounded-tr-sm" />
          <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-blue-500 border-opacity-50 rounded-bl-sm" />
          <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-blue-500 border-opacity-50 rounded-br-sm" />
        </div>
      </div>

      {/* Countdown section */}
      <div className="flex flex-col items-center gap-3">
        <CountdownRing
          secondsRemaining={secondsRemaining}
          ttlSeconds={ttlSeconds}
        />
        <p className={`text-base font-semibold tabular-nums tracking-wide ${
          isUrgent ? 'text-kiosk-amber' : 'kiosk-muted'
        }`}>
          QR refreshes in{' '}
          <span className={`font-bold ${isUrgent ? 'text-kiosk-amber' : 'text-kiosk-blue'}`}>
            {String(Math.floor(secondsRemaining / 60)).padStart(2, '0')}:{String(secondsRemaining % 60).padStart(2, '0')}
          </span>
        </p>
        <p className="kiosk-muted text-sm">
          Each QR code is valid for {ttlSeconds} seconds — screenshots will not work
        </p>
      </div>
    </div>
  );
}